import { useState, useEffect } from 'react';
import { api } from '../services/api';

export interface DeviceAuditLog {
  id: string;
  customer_id: string;
  person_id: string;
  device_id?: string;
  zone_id?: string;
  operation_type: 'sync' | 'remove' | 'auto_revoke_entrada' | 'manual_revoke';
  status: 'success' | 'failed' | 'pending' | 'retrying';
  error_message?: string;
  error_details?: Record<string, any>;
  performed_by?: string;
  performed_at: string;
  completed_at?: string;
  duration_ms?: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    profile_picture_url?: string;
    identity_photo?: { photo_url: string };
  };
  device?: {
    id: string;
    name: string;
  };
  zone?: {
    id: string;
    name: string;
  };
}

export interface AuditLogsFilters {
  person_id?: string;
  device_id?: string;
  operation_type?: string;
  status?: string;
  role_type?: string;
  performed_by?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

export interface AuditLogsResponse {
  data: DeviceAuditLog[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PersonAuditLogsResponse {
  data: DeviceAuditLog[];
  count: number;
}

/**
 * Hook to fetch audit logs for a specific person (last 20 operations)
 * Used in person modal/detail view
 */
export const usePersonAuditLogs = (customerId: string | undefined, personId: string | undefined) => {
  const [logs, setLogs] = useState<DeviceAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId || !personId) {
      setLogs([]);
      return;
    }

    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<PersonAuditLogsResponse>(
          `/customers/${customerId}/people/${personId}/device-audit-logs`
        );
        setLogs(response.data.data || []);
      } catch (err: any) {
        console.error('Failed to fetch person audit logs:', err);
        setError(err.response?.data?.error || 'Failed to load audit logs');
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [customerId, personId]);

  return { logs, loading, error };
};

/**
 * Hook to fetch audit logs for a customer with filters and pagination
 * Used in dedicated audit log page
 */
export const useCustomerAuditLogs = (customerId: string | undefined, filters: AuditLogsFilters = {}) => {
  const [logs, setLogs] = useState<DeviceAuditLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = async () => {
    if (!customerId) {
      setLogs([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await api.get<AuditLogsResponse>(
        `/customers/${customerId}/device-audit-logs?${params.toString()}`
      );

      setLogs(response.data.data || []);
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (err: any) {
      console.error('Failed to fetch customer audit logs:', err);
      setError(err.response?.data?.error || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [customerId, JSON.stringify(filters)]);

  return {
    logs,
    totalCount,
    totalPages,
    loading,
    error,
    refetch: fetchLogs
  };
};

/**
 * Export audit logs to CSV
 */
export const exportAuditLogsToCSV = async (customerId: string, filters: AuditLogsFilters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    // Fetch all logs without pagination
    params.set('page_size', '10000'); // Get all records

    const response = await api.get<AuditLogsResponse>(
      `/customers/${customerId}/device-audit-logs?${params.toString()}`
    );

    const logs = response.data.data || [];

    // Convert to CSV
    const headers = [
      'Data/Hora',
      'Pessoa',
      'Operação',
      'Dispositivo',
      'Zona',
      'Status',
      'Erro',
      'Realizado Por',
      'Duração (ms)'
    ];

    const rows = logs.map(log => [
      new Date(log.performed_at).toLocaleString('pt-BR'),
      log.person ? `${log.person.first_name} ${log.person.last_name}` : '-',
      log.operation_type,
      log.device?.name || '-',
      log.zone?.name || '-',
      log.status,
      log.error_message || '-',
      log.performed_by || 'Automático',
      log.duration_ms?.toString() || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (err) {
    console.error('Failed to export audit logs:', err);
    return false;
  }
};
